/**
 * orgEvents — the subscriber seam on the org event drain (plan §3.3, rung 7).
 *
 * WHY THIS EXISTS. `createResync` (resync.js) takes ONE `onInvalidate` and ONE
 * `onFullInvalidate`, and useOrgRefdata.js hard-wires both to the reference-data
 * store. That was the right shape when reference data was the only consumer;
 * it is no shape at all for a screen-level store (the ledger list) that mounts
 * after the drain is running and unmounts before it stops. This module is the
 * fan-out: the drain publishes each drained page here, and any store that wants
 * the events subscribes for the org it is showing.
 *
 * WHY A MODULE REGISTRY AND NOT A CONTEXT VALUE. Three candidates were on the
 * table — a value on the refdata context, a second `createResync` per
 * consumer, or this. A second resync is a second `events_since` stream with its
 * own cursor, so two consumers could disagree about where the org is (the
 * exact thing resync exists to make impossible). A context value needs
 * context_and_index.jsx to carry and re-publish it, which couples the app shell
 * to every screen store's lifecycle. A registry keyed by org id costs one
 * import on each side, needs no React to test, and the screen hooks already
 * hold the org id they subscribe under.
 *
 * THE CONTRACT (what every consumer may rely on):
 *   - Consumers are invoked SYNCHRONOUSLY, per drained page, in subscription
 *     order, with the page's rows exactly as `events_since` returned them:
 *     `[{seq, topic, entity_id, payload, occurred_at}]`, ascending seq.
 *   - The return value is dropped. A consumer that does async work (the ledger
 *     store's `applyEvents` does) must never let it throw; resync does not
 *     await `onInvalidate` and the cursor has already moved on (resync.js:114).
 *   - A consumer that throws SYNCHRONOUSLY is caught, reported ONCE per
 *     consumer per publish (one drained page), and does not abort the drain or
 *     starve the consumers after it. A throw is a consumer bug; the events are
 *     still gone past the cursor, which is why the ledger store never throws
 *     in the first place.
 *   - On the unrecoverable-gap branch only the full-invalidate subscribers
 *     fire — the events were pruned server-side and there is nothing to apply
 *     (resync.js:120-123). A consumer that needs to survive that branch must
 *     subscribe to BOTH.
 *   - Subscribing is per org id. A store that switches org must re-subscribe;
 *     nothing published for org A ever reaches a subscriber for org B.
 *   - Late subscribers see subsequent pages only. A store that mounts mid-drain
 *     fetched its own first page server-fresh, so it has not missed anything
 *     it needed.
 *
 * WHAT IT DOES NOT DO. It does not buffer, replay, or order across pages, and
 * it does not know what a topic means. It is a Set of functions per org and a
 * loop.
 */
import { captureDataFault } from "./telemetry";

/** orgId → { events: Set<fn>, full: Set<fn> } */
const registries = new Map();

const forOrg = (orgId) => {
  let r = registries.get(orgId);
  if (!r) {
    r = { events: new Set(), full: new Set() };
    registries.set(orgId, r);
  }
  return r;
};

const unsubscribe = (orgId, set, fn) => () => {
  const r = registries.get(orgId);
  if (!r) return;
  r[set].delete(fn);
  if (r.events.size === 0 && r.full.size === 0) registries.delete(orgId);
};

/**
 * Receive every drained page for `orgId`.
 * @param {string} orgId
 * @param {(events: Array<object>) => void} fn
 * @returns {() => void} unsubscribe
 */
export function subscribeToEvents(orgId, fn) {
  if (!orgId || typeof fn !== "function") return () => {};
  forOrg(orgId).events.add(fn);
  return unsubscribe(orgId, "events", fn);
}

/**
 * Receive the unrecoverable-gap signal for `orgId`: drop everything and
 * refetch, because the events between were pruned before we asked.
 * @param {string} orgId
 * @param {() => void} fn
 * @returns {() => void} unsubscribe
 */
export function subscribeToFullInvalidate(orgId, fn) {
  if (!orgId || typeof fn !== "function") return () => {};
  forOrg(orgId).full.add(fn);
  return unsubscribe(orgId, "full", fn);
}

/**
 * One drained page in. Called by useOrgRefdata's `onInvalidate` after the
 * reference-data store has had it. Never throws.
 */
export function publishEvents(orgId, events) {
  const r = registries.get(orgId);
  if (!r || r.events.size === 0) return 0;
  const page = Array.isArray(events) ? events : [];
  let delivered = 0;
  // Snapshot the set: a consumer that unsubscribes (or subscribes) from inside
  // its own callback must not change who receives THIS page.
  for (const fn of [...r.events]) {
    try {
      fn(page);
      delivered += 1;
    } catch (e) {
      report("events", orgId, e, page);
    }
  }
  return delivered;
}

/** The gap branch. Same contract as publishEvents. Never throws. */
export function publishFullInvalidate(orgId) {
  const r = registries.get(orgId);
  if (!r || r.full.size === 0) return 0;
  let delivered = 0;
  for (const fn of [...r.full]) {
    try {
      fn();
      delivered += 1;
    } catch (e) {
      report("full_invalidate", orgId, e, []);
    }
  }
  return delivered;
}

/**
 * A consumer threw. One event per consumer per page, constant message so
 * they group into one issue per branch; the page's seq range and topics say
 * what it was being handed. The counts stay out of tags (unbounded
 * cardinality).
 */
function report(branch, orgId, error, page) {
  captureDataFault(error instanceof Error ? error : new Error(String(error)), {
    component: "org_events",
    action: `publish_${branch}`,
    orgId,
    level: "error",
    context: {
      page_size: page.length,
      from_seq: page[0]?.seq ?? null,
      to_seq: page[page.length - 1]?.seq ?? null,
      topics: [...new Set(page.map((e) => e?.topic).filter(Boolean))].slice(0, 20),
    },
  });
}

/** Test seam: inspect or clear the registry between cases. */
export const __testing = {
  subscriberCounts(orgId) {
    const r = registries.get(orgId);
    return { events: r?.events.size ?? 0, full: r?.full.size ?? 0 };
  },
  reset() { registries.clear(); },
};
